import { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { BulkCreateMenuEntryPayload, BulkCreateMenuResult } from "src/data/admin/types/menu";
import { useBulkCreateMenu } from "src/data/admin/queries/menu";
import RestaurantAndOptionsBar from "./RestaurantAndOptionsBar";
import BulkMenuDropzone from "./BulkMenuDropzone";
import BulkMenuTable from "./BulkMenuTable";
import BulkMenuResultBubble from "./BulkMenuResultBubble";
import { parseCsv } from "./csvParser";

const BulkCreateMenus = () => {
    const [restaurantId, setRestaurantId] = useState("");
    const [replace, setReplace] = useState(false);
    const [entries, setEntries] = useState<BulkCreateMenuEntryPayload[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [result, setResult] = useState<BulkCreateMenuResult | null>(null);

    const { mutate, isPending } = useBulkCreateMenu();

    const handleFile = (file: File) => {
        setParseError(null);
        setEntries(null);
        setResult(null);

        if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
            setParseError("Only CSV files are accepted.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const parsed = parseCsv(e.target?.result as string);
            if (parsed.ok) {
                setEntries(parsed.entries);
            } else {
                setParseError(parsed.error);
            }
        };
        reader.readAsText(file);
    };

    const handleClear = () => {
        setEntries(null);
        setParseError(null);
        setResult(null);
    };

    const handleSubmit = () => {
        if (!entries || !restaurantId) return;
        mutate(
            { restaurantId, payload: { replace, entries } },
            {
                onSuccess: (data) => setResult(data),
                onError: (err) => setParseError(err.message),
            }
        );
    };

    const canSubmit = !!entries && entries.length > 0 && !!restaurantId;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <MenuBookIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="h6">Bulk Create Menus</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Upload a CSV file to create menu items for a restaurant. One row per item.
            </Typography>

            <RestaurantAndOptionsBar
                restaurantId={restaurantId}
                onRestaurantChange={setRestaurantId}
                replace={replace}
                onReplaceChange={setReplace}
            />

            <Box mt={3}>
                <BulkMenuDropzone entries={entries} onFile={handleFile} />
            </Box>

            {parseError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {parseError}
                </Alert>
            )}

            {entries && entries.length > 0 && (
                <BulkMenuTable entries={entries} />
            )}

            <Box mt={3} display="flex" alignItems="center" gap={2}>
                <CustomLoadingButton
                    loading={isPending}
                    disabled={!canSubmit}
                    onSubmit={handleSubmit}
                    label={isPending ? "Creating…" : `Create ${entries?.length ?? 0} Item${(entries?.length ?? 0) !== 1 ? "s" : ""}`}
                    color="success"
                    fullwidth={false}
                />
                {entries && (
                    <CustomButton color="inverted" onClick={handleClear}>
                        Clear
                    </CustomButton>
                )}
            </Box>

            {result && (
                <BulkMenuResultBubble
                    result={result}
                    onDismiss={() => setResult(null)}
                />
            )}
        </Box>
    );
};

export default BulkCreateMenus;
